import React, { useEffect, useState, memo, useRef } from "react";
import toast from "react-hot-toast";
import {
  branchService,
  categoryService,
  companyService,
  departmentService,
  designationService,
  gradesService,
  reasonsService,
  subDepartmentService,
  employeeService,
} from "../../../services/api";
import CompanyModal from "./CompanyTable";
import { INDIAN_STATES } from "../employee-management/salary-structure/utils/ptStates";
import {
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Plus,
  Search,
  Settings,
  Lock,
  Info,
} from "lucide-react";
import Modal from "./Modal";
import CategoryMinimumWagesModal from "./CategoryMinimumWagesModal";
import CategoryMinimumWagesImportModal from "./CategoryMinimumWagesImportModal";
import Enterprise from "../Enterprise";
import Filters from "../Filters";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { displayLabel } from "../../../utils/displayLabel";
import {
  getAuthItem,
  getAuthJson,
  setAuthJson,
} from "../../../utils/authStorage";
import {
  DsBadge,
  DsButton,
  DsIcon,
  DsInput,
} from "../../../design-system";

/** Resolve active company id from auth storage (currentUser / companyData / companyId). */
function resolveCompanyId() {
  const direct = getAuthItem("companyId");
  if (direct && direct !== "null" && direct !== "undefined") {
    const n = Number(direct);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const currentUser = getAuthJson("currentUser");
  if (currentUser?.companyId) return Number(currentUser.companyId);
  if (currentUser?.company_id) return Number(currentUser.company_id);
  const companyData = getAuthJson("companyData");
  if (companyData?.id) return Number(companyData.id);
  if (companyData?.companyId) return Number(companyData.companyId);
  try {
    const legacyUser = JSON.parse(localStorage.getItem("user") || "null");
    if (legacyUser?.companyId) return Number(legacyUser.companyId);
  } catch {
    /* ignore */
  }
  return 0;
}

export {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./companyDocumentDefaults";

const initialCompanies = [
  {
    id: 1,
    code: "C001",
    name: "ABC Pvt Ltd",
    email: "abc@example.com",
    password: "password123",
    phone: "1234567890",
  },
  {
    id: 2,
    code: "C002",
    name: "XYZ Corp",
    email: "xyz@example.com",
    password: "password456",
    phone: "9876543210",
  },
  ...Array.from({ length: 18 }, (_, i) => ({
    id: i + 3,
    code: `C00${i + 3}`,
    name: `Company ${i + 3}`,
    email: `company${i + 3}@example.com`,
    password: `password${i + 3}`,
    phone: `123456789${i}`,
  })),
];

export function ReportsScreen() {
  const [showFilters, setShowFilters] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [darkMode, setDarkMode] = useState(false);

  // Sample employee data for testing
  const sampleEmployees = [
    {
      id: 1,
      empCode: "EMP001",
      email: "john.doe@company.com",
      department: { name: "Engineering" },
      designation: { name: "Software Engineer" },
      branch: { name: "Head Office" },
      employmentType: "full-time",
      employmentStatus: "confirmed",
      ctc: 800000,
      user: {
        name: "John Doe",
        phone: "9876543210",
        dateOfBirth: "1990-01-15",
        gender: "Male",
      },
    },
    {
      id: 2,
      empCode: "EMP002",
      email: "jane.smith@company.com",
      department: { name: "HR" },
      designation: { name: "HR Manager" },
      branch: { name: "Branch Office" },
      employmentType: "full-time",
      employmentStatus: "probation",
      ctc: 600000,
      user: {
        name: "Jane Smith",
        phone: "9876543211",
        dateOfBirth: "1992-05-20",
        gender: "Female",
      },
    },
    {
      id: 3,
      empCode: "EMP003",
      email: "bob.wilson@company.com",
      department: { name: "Sales" },
      designation: { name: "Sales Executive" },
      branch: { name: "Head Office" },
      employmentType: "contract",
      employmentStatus: "confirmed",
      ctc: 500000,
      user: {
        name: "Bob Wilson",
        phone: "9876543212",
        dateOfBirth: "1988-11-30",
        gender: "Male",
      },
    },
  ];

  const handleApplyFilters = (filteredEmployees, currentFilters) => {
    console.log("Applied filters:", currentFilters);
    console.log("Filtered employees:", filteredEmployees);

    // Use sample data if no employees returned from API
    if (filteredEmployees && filteredEmployees.length > 0) {
      setEmployees(filteredEmployees);
    } else {
      // Apply basic filtering on sample data for demo
      const filtered = sampleEmployees.filter((emp) => {
        if (currentFilters.branch && emp.branch?.name !== currentFilters.branch)
          return false;
        if (
          currentFilters.department &&
          emp.department?.name !== currentFilters.department
        )
          return false;
        if (
          currentFilters.employmentType &&
          emp.employmentType !== currentFilters.employmentType
        )
          return false;
        if (
          currentFilters.employmentStatus &&
          emp.employmentStatus !== currentFilters.employmentStatus
        )
          return false;
        return true;
      });
      setEmployees(filtered);
    }

    setShowFilters(false);
  };

  return (
    <div
      className={`min-h-[calc(100dvh-5rem)] ${
        darkMode ? "bg-gray-900 text-white" : "bg-redhalo-gray/30 text-gray-900"
      }`}
    >
      {/* Header */}
      <div
        className={`p-6 border-b ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-xl font-bold uppercase tracking-wide2 text-gray-900 dark:text-white">
            Employee Reports
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setDarkMode(!darkMode)}
              className={`rounded-md border px-4 py-2 text-sm font-semibold transition-colors ${
                darkMode
                  ? "border-[#4C4C4C] bg-[#2C2C2C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                  : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
              }`}
            >
              {darkMode ? "Light Mode" : "Dark Mode"}
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(true)}
              className="rounded-md px-6 py-2 text-sm font-semibold rh-btn-primary shadow-sm transition-all hover:opacity-95"
            >
              Open Filters
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            className={`p-4 rounded-lg ${
              darkMode ? "bg-gray-800" : "bg-white border"
            }`}
          >
            <h3 className="text-lg font-semibold">Total Employees</h3>
            <p className="text-2xl font-bold text-redhalo-red">
              {employees.length}
            </p>
          </div>
          <div
            className={`p-4 rounded-lg ${
              darkMode ? "bg-gray-800" : "bg-white border"
            }`}
          >
            <h3 className="text-lg font-semibold">Sample Data</h3>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {sampleEmployees.length}
            </p>
          </div>
        </div>

        {/* Employees Table */}
        <div
          className={`overflow-hidden rounded-table-lg border shadow-card ${
            darkMode ? "border-[#4C4C4C] bg-[#1a1a1a]" : "border-gray-200 bg-[#fafafa]"
          }`}
        >
          <table className="w-full text-sm">
            <thead
              className={
                darkMode ? "rh-table-header-row-dark" : "rh-table-header-row"
              }
            >
              <tr>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Employee ID
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Name
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Department
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Designation
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Branch
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Type
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Status
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  CTC
                </th>
              </tr>
            </thead>
            <tbody>
              {employees.length > 0 ? (
                employees.map((employee, idx) => (
                  <tr
                    key={employee.id}
                    className={`border-b transition-colors ${
                      darkMode
                        ? `border-[#4C4C4C] ${
                            idx % 2 === 0 ? "bg-[#252525]" : "bg-[#1f1f1f]"
                          } hover:bg-[#333333]`
                        : `border-gray-300 ${
                            idx % 2 === 0 ? "bg-gray-100/80" : "bg-[#fafafa]"
                          } hover:bg-redhalo-mint/40`
                    }`}
                  >
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                      {displayLabel(employee.empCode)}
                    </td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                      {displayLabel(employee.user?.name)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {displayLabel(employee.department?.name || employee.department)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {displayLabel(employee.designation?.name || employee.designation)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {displayLabel(employee.branch?.name || employee.branch)}
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-700 dark:text-gray-300">
                      {employee.employmentType}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${
                          employee.employmentStatus === "confirmed"
                            ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200"
                            : employee.employmentStatus === "probation"
                            ? "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {employee.employmentStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      ₹{employee.ctc?.toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-gray-500">
                    No employees found. Try applying different filters or check
                    the sample data.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sample Data Section for Testing */}
        <div className="mt-8">
          <h2
            className={`text-xl font-bold mb-4 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Sample Data (For Testing)
          </h2>
          <div
            className={`overflow-hidden rounded-table-lg border shadow-card ${
              darkMode ? "border-[#4C4C4C] bg-[#1a1a1a]" : "border-gray-200 bg-[#fafafa]"
            }`}
          >
            <table className="w-full text-sm">
              <thead
                className={
                  darkMode ? "rh-table-header-row-dark" : "rh-table-header-row"
                }
              >
                <tr>
                  <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                    Employee ID
                  </th>
                  <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                    Department
                  </th>
                  <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                    Branch
                  </th>
                </tr>
              </thead>
              <tbody>
                {sampleEmployees.map((employee, idx) => (
                  <tr
                    key={employee.id}
                    className={`border-b transition-colors ${
                      darkMode
                        ? `border-[#4C4C4C] ${
                            idx % 2 === 0 ? "bg-[#252525]" : "bg-[#1f1f1f]"
                          } hover:bg-[#333333]`
                        : `border-gray-300 ${
                            idx % 2 === 0 ? "bg-gray-100/80" : "bg-[#fafafa]"
                          } hover:bg-redhalo-mint/40`
                    }`}
                  >
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                      {employee.empCode}
                    </td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                      {employee.user?.name}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {employee.department?.name}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {employee.branch?.name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Filters Modal */}
      {showFilters && (
        <Filters
          onApply={handleApplyFilters}
          onClose={() => setShowFilters(false)}
          darkMode={darkMode}
          initialFilters={{}}
        />
      )}
    </div>
  );
}

function Masters({
  darkMode,
  setCurrentPage,
  toggleDarkMode,
  initialTab = "enterprise",
  allowedTabs = ["enterprise", "masters"],
}) {
    const { hasScreenAccess, isAdmin } = usePermissions();
    const canCreateMaster =
      isAdmin || hasScreenAccess("configuration", "masters", "create");
    const canEditMaster =
      isAdmin || hasScreenAccess("configuration", "masters", "edit");
    const canDeactivateMaster =
      isAdmin || hasScreenAccess("configuration", "masters", "delete");
    const hasAnyMasterWriteAccess =
      canCreateMaster || canEditMaster || canDeactivateMaster;

    const normalizedTabs =
      Array.isArray(allowedTabs) && allowedTabs.length > 0
        ? allowedTabs
        : ["enterprise", "masters"];
    const [activeTab, setActiveTab] = useState(
      normalizedTabs.includes(initialTab) ? initialTab : normalizedTabs[0]
    );
    const [companies, setCompanies] = useState(initialCompanies);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [subDepartments, setSubDepartments] = useState([]);
    const [grades, setGrades] = useState([]);
    const [categories, setCategories] = useState([]);
    const [followMinimumWages, setFollowMinimumWages] = useState(false);
    const [categoryMwCurrentById, setCategoryMwCurrentById] = useState({});
    const [categoryMwModal, setCategoryMwModal] = useState({
      isOpen: false,
      category: null,
    });
    const [mwImportOpen, setMwImportOpen] = useState(false);
    const [resignationReasons, setResignationReasons] = useState([]);
    const [terminationReasons, setTerminationReasons] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [isCreateCompanyOpen, setIsCreateCompanyOpen] = useState(false);
    const [isEditCompanyOpen, setIsEditCompanyOpen] = useState(false);
    const [searchTerms, setSearchTerms] = useState({
      branch: "",
      department: "",
      designation: "",
      subDepartment: "",
      grade: "",
      category: "",
      reasons: "",
    });
    const [formData, setFormData] = useState({
      code: "",
      name: "",
      address: "",
      email: "",
      phone: "",
      password: "",
      pfCode: "",
      esiCode: "",
      labourLicense: "",
      domainName: "",
      contactPerson: "",
      website: "",
      superAdminID: "",
      logo: null,
    });
    const [inviteAdmin, setInviteAdmin] = useState(false);

    const [confirmState, setConfirmState] = useState({
      isOpen: false,
      masterType: null,
      item: null,
    });

    const [duplicateInactiveState, setDuplicateInactiveState] = useState({
      isOpen: false,
      masterType: null,
      existing: null,
    });

    const masterModalSubmitLockRef = useRef(false);

    // Modal states for each master
    const [modalStates, setModalStates] = useState({
      branch: {
        isOpen: false,
        editing: null,
        data: { name: "", address: "", status: "active" },
      },
      department: {
        isOpen: false,
        editing: null,
        data: { name: "", departmentHead: "", status: "active" },
      },
      designation: {
        isOpen: false,
        editing: null,
        data: {
          name: "",
          departmentId: null,
          jobDescription: {
            heading: "",
            description: "",
            experience: "",
            qualifications: "",
          },
          salaryRangeFrom: "",
          salaryRangeTo: "",
          status: "active",
        },
      },
      subDepartment: {
        isOpen: false,
        editing: null,
        data: { name: "", departmentId: "", status: "active" },
      },
      grade: {
        isOpen: false,
        editing: null,
        data: { name: "", status: "active" },
      },
      category: {
        isOpen: false,
        editing: null,
        data: {
          name: "",
          status: "active",
          minimumWage: "",
          mwEffectiveMonth: new Date().toISOString().slice(0, 7),
        },
      },
      reasons: {
        isOpen: false,
        editing: null,
        data: { name: "", type: "resignation", status: "active" },
      },
    });

    // Show deactivated items state
    const [showDeactivated, setShowDeactivated] = useState({
      branch: false,
      department: false,
      designation: false,
      subDepartment: false,
      grade: false,
      category: false,
      reasons: false,
    });
    const [activeMasterSubTab, setActiveMasterSubTab] = useState("branch");

    // Actions dropdown: show on click instead of hover (key: `${tabName}-${item.id}` or null)
    const [openActionsKey, setOpenActionsKey] = useState(null);
    const openActionsRef = React.useRef(null);

    // Close actions dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (openActionsKey != null && openActionsRef.current && !openActionsRef.current.contains(e.target)) {
          setOpenActionsKey(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openActionsKey]);

    const handleSearchChange = (masterType, value) => {
      setSearchTerms((prev) => ({
        ...prev,
        [masterType]: value,
      }));
    };

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleLogoUpload = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () =>
          setFormData((prev) => ({ ...prev, logo: reader.result }));
        reader.readAsDataURL(file);
      }
    };

    const handleInviteAdminChange = (e) => setInviteAdmin(e.target.checked);

    const handleCreateCompanySubmit = () => {
      const newCompany = { id: companies.length + 1, ...formData };
      setCompanies((prev) => [...prev, newCompany]);
      handleCreateCompanyClose();
    };

    const handleSaveNew = () => {
      const newCompany = { id: companies.length + 1, ...formData };
      setCompanies((prev) => [...prev, newCompany]);
      handleReset();
    };

    const handleReset = () => {
      setFormData({
        code: "",
        name: "",
        address: "",
        email: "",
        phone: "",
        password: "",
        pfCode: "",
        esiCode: "",
        labourLicense: "",
        domainName: "",
        contactPerson: "",
        website: "",
        superAdminID: "",
        logo: null,
      });
      setInviteAdmin(false);
    };

    const handleCreateCompanyClose = () => {
      setIsCreateCompanyOpen(false);
      handleReset();
    };

    const handleUpdateCompany = () => {
      setIsEditCompanyOpen(false);
      handleReset();
    };

    // Master item actions
    const handleCreateItem = (masterType) => {
      if (!canCreateMaster) {
        toast.error("You do not have permission to create master records.");
        return;
      }
      const defaults = getDefaultData(masterType);
      setModalStates((prev) => ({
        ...prev,
        [masterType]: {
          ...prev[masterType],
          isOpen: true,
          editing: null,
          data: defaults,
        },
      }));
    };

    const handleEditItem = async (masterType, item) => {
      if (!canEditMaster) {
        toast.error("You do not have permission to edit master records.");
        return;
      }
      const defaultData = getDefaultData(masterType);
      let data = { ...item };
      if (masterType === "designation") {
        const jd = item.jobDescription;
        const jobDescription =
          jd && typeof jd === "object"
            ? {
                heading: jd.heading ?? "",
                description: jd.description ?? "",
                experience: jd.experience ?? "",
                qualifications: jd.qualifications ?? "",
              }
            : {
                heading: typeof jd === "string" ? jd : "",
                description: "",
                experience: item.experience ?? "",
                qualifications: item.qualifications ?? "",
              };
        data = {
          ...defaultData,
          ...item,
          jobDescription,
          salaryRangeFrom: item.salaryRangeFrom ?? item.salary_range_from ?? "",
          salaryRangeTo: item.salaryRangeTo ?? item.salary_range_to ?? "",
        };
      }
      if (masterType === "category") {
        data = {
          ...defaultData,
          ...item,
          minimumWage: "",
          mwEffectiveMonth: new Date().toISOString().slice(0, 7),
        };
        if (followMinimumWages) {
          try {
            const mwRes = await categoryService.getMinimumWages(item.id);
            const current = mwRes?.data?.current;
            if (current) {
              data.minimumWage =
                current.amount != null ? String(current.amount) : "";
              data.mwEffectiveMonth =
                current.effectiveMonth || data.mwEffectiveMonth;
            }
          } catch {
            /* keep empty MW fields */
          }
        }
      }
      if (masterType === "branch") {
        data = {
          ...defaultData,
          ...item,
          state: item.state || "",
        };
      }
      setModalStates((prev) => ({
        ...prev,
        [masterType]: {
          ...prev[masterType],
          isOpen: true,
          editing: item,
          data,
        },
      }));
    };

    const performDeleteItem = async (masterType, item) => {
      let response;

      switch (masterType) {
        case "branch":
          response = await branchService.deleteBranch(item.id);
          break;
        case "department":
          response = await departmentService.deleteDepartment(item.id);
          break;
        case "designation":
          response = await designationService.deleteDesignation(item.id);
          break;
        case "subDepartment":
          response = await subDepartmentService.deleteSubDepartment(item.id);
          break;
        case "grade":
          response = await gradesService.deleteGrade(item.id);
          break;
        case "category":
          response = await categoryService.deleteCategory(item.id);
          break;
        case "reasons":
          response = await reasonsService.deleteReason(item.id);
          break;
        default:
          throw new Error(`Unknown master type: ${masterType}`);
      }

      if (!response.success) {
        throw new Error(response.message || "Delete failed");
      }
    };

    const handleDeleteItem = async (masterType, item) => {
      setConfirmState({ isOpen: true, masterType, item });
    };

    const handleConfirmDelete = async () => {
      const { masterType, item } = confirmState;
      if (!masterType || !item) {
        setConfirmState({ isOpen: false, masterType: null, item: null });
        return;
      }

      try {
        await performDeleteItem(masterType, item);
        toast.success(`${masterType} deleted successfully`);
        await fetchMasterData(masterType, showDeactivated[masterType]);
      } catch (error) {
        console.error(`Error deleting ${masterType}:`, error);
        toast.error(error.message || "Error deleting item");
      } finally {
        setConfirmState({ isOpen: false, masterType: null, item: null });
      }
    };

    const handleActivateDuplicate = async () => {
      const { masterType, existing } = duplicateInactiveState;
      if (!masterType || !existing?.id) {
        setDuplicateInactiveState({
          isOpen: false,
          masterType: null,
          existing: null,
        });
        return;
      }

      try {
        let response;
        const updateData = { ...existing, status: "active" };

        switch (masterType) {
          case "branch":
            response = await branchService.updateBranch(existing.id, updateData);
            break;
          case "department":
            response = await departmentService.updateDepartment(
              existing.id,
              updateData
            );
            break;
          case "designation":
            response = await designationService.updateDesignation(
              existing.id,
              updateData
            );
            break;
          case "subDepartment":
            response = await subDepartmentService.updateSubDepartment(
              existing.id,
              updateData
            );
            break;
          case "grade":
            response = await gradesService.updateGrade(existing.id, updateData);
            break;
          case "category":
            response = await categoryService.updateCategory(
              existing.id,
              updateData
            );
            break;
          case "reasons":
            response = await reasonsService.updateReason(existing.id, updateData);
            break;
          default:
            throw new Error(`Unknown master type: ${masterType}`);
        }

        if (!response?.success) {
          throw new Error(response?.message || "Activation failed");
        }

        toast.success(`${existing.name} activated successfully`);
        await fetchMasterData(masterType, showDeactivated[masterType]);

        setModalStates((prev) => ({
          ...prev,
          [masterType]: {
            isOpen: false,
            editing: null,
            data: getDefaultData(masterType),
          },
        }));
      } catch (error) {
        console.error(`Error activating ${masterType}:`, error);
        toast.error(error.message || "Error activating item");
      } finally {
        setDuplicateInactiveState({
          isOpen: false,
          masterType: null,
          existing: null,
        });
      }
    };

    const handleDeactivateItem = async (masterType, item) => {
      if (!canDeactivateMaster) {
        toast.error("You do not have permission to deactivate master records.");
        return;
      }
      try {
        const updateData = { ...item, status: "inactive" };
        let response;

        switch (masterType) {
          case "branch":
            response = await branchService.updateBranch(item.id, updateData);
            break;
          case "department":
            response = await departmentService.updateDepartment(
              item.id,
              updateData
            );
            break;
          case "designation":
            response = await designationService.updateDesignation(
              item.id,
              updateData
            );
            break;
          case "subDepartment":
            response = await subDepartmentService.updateSubDepartment(
              item.id,
              updateData
            );
            break;
          case "grade":
            response = await gradesService.updateGrade(item.id, updateData);
            break;
          case "category":
            response = await categoryService.updateCategory(
              item.id,
              updateData
            );
            break;
          case "reasons":
            response = await reasonsService.updateReason(item.id, updateData);
            break;
          default:
            throw new Error(`Unknown master type: ${masterType}`);
        }

        if (response.success) {
          toast.success(`${item.name} deactivated successfully`);
          // Refresh data from backend - fetch all items if showDeactivated is true
          await fetchMasterData(masterType, showDeactivated[masterType]);
        } else {
          throw new Error(response.message || "Deactivation failed");
        }
      } catch (error) {
        console.error(`Error deactivating ${masterType}:`, error);
        toast.error(error.message || "Error deactivating item");
      }
    };

    const handleReactivateItem = async (masterType, item) => {
      if (!canDeactivateMaster) {
        toast.error("You do not have permission to reactivate master records.");
        return;
      }
      try {
        const updateData = { ...item, status: "active" };
        let response;

        switch (masterType) {
          case "branch":
            response = await branchService.updateBranch(item.id, updateData);
            break;
          case "department":
            response = await departmentService.updateDepartment(
              item.id,
              updateData
            );
            break;
          case "designation":
            response = await designationService.updateDesignation(
              item.id,
              updateData
            );
            break;
          case "subDepartment":
            response = await subDepartmentService.updateSubDepartment(
              item.id,
              updateData
            );
            break;
          case "grade":
            response = await gradesService.updateGrade(item.id, updateData);
            break;
          case "category":
            response = await categoryService.updateCategory(
              item.id,
              updateData
            );
            break;
          case "reasons":
            response = await reasonsService.updateReason(item.id, updateData);
            break;
          default:
            throw new Error(`Unknown master type: ${masterType}`);
        }

        if (response.success) {
          toast.success(`${item.name} reactivated successfully`);
          // Refresh data from backend - fetch all items if showDeactivated is true
          await fetchMasterData(masterType, showDeactivated[masterType]);
        } else {
          throw new Error(response.message || "Reactivation failed");
        }
      } catch (error) {
        console.error(`Error reactivating ${masterType}:`, error);
        toast.error(error.message || "Error reactivating item");
      }
    };

    const handleModalSubmit = async (masterType) => {
      const modalState = modalStates[masterType];
      const { data, editing } = modalState;

      if (!data.name) {
        toast.error("Name is required");
        return;
      }

      // Defense-in-depth: the Create/Edit entry points are already gated but
      // guard the submit handler too so a direct call can't bypass RBAC.
      if (editing) {
        if (!canEditMaster) {
          toast.error("You do not have permission to edit master records.");
          return;
        }
      } else {
        if (!canCreateMaster) {
          toast.error("You do not have permission to create master records.");
          return;
        }
      }

      if (masterModalSubmitLockRef.current) {
        toast.error("Please wait — save is already in progress.", {
          position: "top-right",
        });
        return;
      }
      masterModalSubmitLockRef.current = true;

      // Strip MW fields from category payload (saved via separate API)
      const {
        minimumWage,
        mwEffectiveMonth,
        mwBranchId,
        ...restData
      } = data;
      const payload =
        masterType === "category"
          ? restData
          : masterType === "branch"
            ? {
                name: data.name,
                address: data.address || "No Address Provided",
                state: data.state || null,
                status: data.status || "active",
              }
            : data;

      const persistCategoryMw = async (categoryId) => {
        if (!followMinimumWages || !categoryId) return;
        if (minimumWage === "" || minimumWage == null) return;
        const amt = Number(minimumWage);
        if (!Number.isFinite(amt) || amt < 0) {
          toast.error("Enter a valid Minimum Wage amount.");
          throw new Error("Invalid minimum wage");
        }
        const month =
          mwEffectiveMonth && /^\d{4}-\d{2}$/.test(mwEffectiveMonth)
            ? mwEffectiveMonth
            : new Date().toISOString().slice(0, 7);
        await categoryService.addMinimumWage(categoryId, {
          amount: amt,
          effectiveMonth: month,
          branchId: mwBranchId === "" || mwBranchId == null ? null : Number(mwBranchId),
        });
      };

      try {
        let response;

        if (editing) {
          // Update existing item
          switch (masterType) {
            case "branch":
              response = await branchService.updateBranch(editing.id, payload);
              break;
            case "department":
              response = await departmentService.updateDepartment(
                editing.id,
                payload
              );
              break;
            case "designation":
              response = await designationService.updateDesignation(
                editing.id,
                payload
              );
              break;
            case "subDepartment":
              response = await subDepartmentService.updateSubDepartment(
                editing.id,
                payload
              );
              break;
            case "grade":
              response = await gradesService.updateGrade(editing.id, payload);
              break;
            case "category":
              response = await categoryService.updateCategory(editing.id, payload);
              break;
            case "reasons":
              response = await reasonsService.updateReason(editing.id, payload);
              break;
            default:
              throw new Error(`Unknown master type: ${masterType}`);
          }

          if (response.success) {
            if (masterType === "category") {
              await persistCategoryMw(editing.id);
            }
            toast.success(`${masterType} updated successfully`);
            // Refresh data from backend - fetch all items if showDeactivated is true
            await fetchMasterData(masterType, showDeactivated[masterType]);
            if (masterType === "category") {
              setCategories((prev) => [...prev]);
            }
          } else {
            throw new Error(response.message || "Update failed");
          }
        } else {
          // Create new item
          switch (masterType) {
            case "branch":
              response = await branchService.createBranch(payload);
              break;
            case "department":
              response = await departmentService.createDepartment(payload);
              break;
            case "designation":
              response = await designationService.createDesignation(payload);
              break;
            case "subDepartment":
              response = await subDepartmentService.createSubDepartment(payload);
              break;
            case "grade":
              response = await gradesService.createGrade(payload);
              break;
            case "category":
              response = await categoryService.createCategory(payload);
              break;
            case "reasons":
              response = await reasonsService.createReason(payload);
              break;
            default:
              throw new Error(`Unknown master type: ${masterType}`);
          }

          if (response.success) {
            if (masterType === "category") {
              const newId = response.data?.id;
              await persistCategoryMw(newId);
            }
            toast.success(`${masterType} created successfully`);
            // Refresh data from backend - fetch all items if showDeactivated is true
            await fetchMasterData(masterType, showDeactivated[masterType]);
            if (masterType === "category") {
              setCategories((prev) => [...prev]);
            }
          } else {
            throw new Error(response.message || "Creation failed");
          }
        }

        // Close modal and reset
        setModalStates((prev) => ({
          ...prev,
          [masterType]: {
            isOpen: false,
            editing: null,
            data: getDefaultData(masterType),
          },
        }));
      } catch (error) {
        const serverData = error?.response?.data;
        if (serverData?.code === "DUPLICATE_INACTIVE" && serverData?.existing) {
          setDuplicateInactiveState({
            isOpen: true,
            masterType,
            existing: serverData.existing,
          });
          return;
        }
        console.error(
          `Error ${editing ? "updating" : "creating"} ${masterType}:`,
          error
        );
        toast.error(
          error.message ||
            `Error ${editing ? "updating" : "creating"} ${masterType}`
        );
      } finally {
        masterModalSubmitLockRef.current = false;
      }
    };

    const getDefaultData = (masterType) => {
      switch (masterType) {
        case "branch":
          return { name: "", address: "", state: "", status: "active" };
        case "department":
          return { name: "", departmentHead: "", status: "active" };
        case "designation":
          return {
            name: "",
            departmentId: null,
            jobDescription: {
              heading: "",
              description: "",
              experience: "",
              qualifications: "",
            },
            salaryRangeFrom: "",
            salaryRangeTo: "",
            status: "active",
          };
        case "subDepartment":
          return { name: "", departmentId: "", status: "active" };
        case "grade":
          return { name: "", status: "active" };
        case "category":
          return {
            name: "",
            status: "active",
            minimumWage: "",
            mwEffectiveMonth: new Date().toISOString().slice(0, 7),
          };
        case "reasons":
          return { name: "", type: "resignation", status: "active" };
        default:
          return { name: "", status: "active" };
      }
    };

    const handleModalClose = (masterType) => {
      setModalStates((prev) => ({
        ...prev,
        [masterType]: {
          isOpen: false,
          editing: null,
          data: getDefaultData(masterType),
        },
      }));
    };

    const fetchMasterData = async (masterType, includeInactive = false) => {
      try {
        switch (masterType) {
          case "branch": {
            const branchResponse = await branchService.getBranches(
              includeInactive
            );
            if (branchResponse.success) {
              setBranches(branchResponse.data);
            }
            break;
          }
          case "department": {
            const deptResponse = await departmentService.getDepartments(
              includeInactive
            );
            if (deptResponse.success) {
              setDepartments(deptResponse.data);
            }
            break;
          }
          case "designation": {
            const designationResponse =
              await designationService.getDesignations(includeInactive);
            if (designationResponse.success) {
              setDesignations(designationResponse.data);
            }
            break;
          }
          case "subDepartment": {
            const subDeptResponse =
              await subDepartmentService.getAllSubDepartments(includeInactive);
            if (subDeptResponse.success) {
              setSubDepartments(subDeptResponse.data);
            }
            break;
          }
          case "grade": {
            const gradeResponse = await gradesService.getGrades(
              includeInactive
            );
            if (gradeResponse.success) {
              setGrades(gradeResponse.data);
            }
            break;
          }
          case "category": {
            const categoryResponse = await categoryService.getAllCategories(
              includeInactive
            );
            if (categoryResponse.success) {
              setCategories(categoryResponse.data);
            }
            break;
          }
          case "reasons": {
            const resignationResponse =
              await reasonsService.getAllResignationReasons(includeInactive);
            const terminationResponse =
              await reasonsService.getAllTerminationReasons(includeInactive);
            if (resignationResponse.success) {
              setResignationReasons(resignationResponse.data);
            }
            if (terminationResponse.success) {
              setTerminationReasons(terminationResponse.data);
            }
            break;
          }
          default:
            break;
        }
      } catch (error) {
        console.error(`Error fetching ${masterType} data:`, error);
      }
    };

    useEffect(() => {
      const fetchData = async (service, setter, errorMessage) => {
        try {
          setLoading(true);
          const response = await service();
          if (response.success) {
            setter(response.data);
          } else {
            toast.error(response.message || errorMessage);
          }
        } catch (error) {
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      };

      // Fetch employees for department head selection
      fetchData(
        employeeService.getEmployees,
        setEmployees,
        "Error fetching employees"
      );

      // Fetch all masters data
      fetchData(
        branchService.getBranches,
        setBranches,
        "Error fetching branches"
      );
      fetchData(
        departmentService.getDepartments,
        setDepartments,
        "Error fetching departments"
      );
      fetchData(
        designationService.getDesignations,
        setDesignations,
        "Error fetching designations"
      );
      fetchData(
        subDepartmentService.getAllSubDepartments,
        setSubDepartments,
        "Error fetching sub-departments"
      );
      fetchData(gradesService.getGrades, setGrades, "Error fetching grades");
      fetchData(
        categoryService.getAllCategories,
        setCategories,
        "Error fetching categories"
      );
      fetchData(
        reasonsService.getAllResignationReasons,
        setResignationReasons,
        "Error fetching resignation reasons"
      );
      fetchData(
        reasonsService.getAllTerminationReasons,
        setTerminationReasons,
        "Error fetching termination reasons"
      );

      const loadMwSetting = async () => {
        try {
          const companyId = resolveCompanyId();
          if (!companyId) {
            // Fall back to cached company flag if id missing
            const cached = getAuthJson("companyData");
            setFollowMinimumWages(!!cached?.followMinimumWages);
            return;
          }
          const res = await companyService.getMinimumWagesSetting(companyId);
          const enabled = !!(
            res?.data?.followMinimumWages ??
            res?.followMinimumWages ??
            res?.company?.followMinimumWages
          );
          setFollowMinimumWages(enabled);
          // Keep companyData cache in sync for other screens
          const cached = getAuthJson("companyData");
          if (cached && typeof cached === "object") {
            setAuthJson("companyData", {
              ...cached,
              followMinimumWages: enabled,
            });
          }
        } catch (err) {
          console.warn("Failed to load Minimum Wages setting:", err);
          const cached = getAuthJson("companyData");
          setFollowMinimumWages(!!cached?.followMinimumWages);
        }
      };
      loadMwSetting();

      const onMwSettingChanged = () => loadMwSetting();
      window.addEventListener("pacehrm:minimum-wages-setting-changed", onMwSettingChanged);
      return () => {
        window.removeEventListener(
          "pacehrm:minimum-wages-setting-changed",
          onMwSettingChanged
        );
      };
    }, []);

    // Re-check MW flag whenever Category master is opened
    useEffect(() => {
      if (activeTab !== "masters" || activeMasterSubTab !== "category") return;
      let cancelled = false;
      (async () => {
        try {
          const companyId = resolveCompanyId();
          if (!companyId) {
            const cached = getAuthJson("companyData");
            if (!cancelled) setFollowMinimumWages(!!cached?.followMinimumWages);
            return;
          }
          const res = await companyService.getMinimumWagesSetting(companyId);
          const enabled = !!(
            res?.data?.followMinimumWages ?? res?.followMinimumWages
          );
          if (!cancelled) setFollowMinimumWages(enabled);
        } catch {
          const cached = getAuthJson("companyData");
          if (!cancelled) setFollowMinimumWages(!!cached?.followMinimumWages);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [activeTab, activeMasterSubTab]);

    // Enrich category rows with current MW when org follows MW
    useEffect(() => {
      if (!followMinimumWages || !categories.length) {
        setCategoryMwCurrentById({});
        return;
      }
      let cancelled = false;
      const loadCurrents = async () => {
        const map = {};
        await Promise.all(
          categories.map(async (cat) => {
            try {
              const res = await categoryService.getMinimumWages(cat.id);
              if (res?.data?.current) {
                map[cat.id] = res.data.current;
              }
            } catch {
              /* ignore per-row */
            }
          })
        );
        if (!cancelled) setCategoryMwCurrentById(map);
      };
      loadCurrents();
      return () => {
        cancelled = true;
      };
    }, [followMinimumWages, categories]);

    const masterTabs = [
      "branch",
      "department",
      "designation",
      "subDepartment",
      "grade",
      "category",
      "reasons",
    ];
    
    // Refetch data when showDeactivated changes while on Masters tab
    useEffect(() => {
      if (activeTab !== "masters") return;
      if (activeTab !== "masters") return;
      masterTabs.forEach((tabName) => {
        if (showDeactivated[tabName]) {
          fetchMasterData(tabName, true);
        } else {
          fetchMasterData(tabName, false);
        }
      });
    }, [showDeactivated, activeTab]);

    const tabs = normalizedTabs;
    const isEmbedded = tabs.length === 1;

    useEffect(() => {
      if (activeTab !== "masters") return;
      if (!masterTabs.includes(activeMasterSubTab)) {
        setActiveMasterSubTab(masterTabs[0]);
      }
    }, [activeTab, activeMasterSubTab]);

    const renderMasterCard = (tabName) => {
      const cardData = {
        branch: {
          title: "Branch",
          infoTooltip: "Manage company branches",
          count: branches.length,
          data: branches,
          searchTerm: searchTerms.branch,
        },
        department: {
          title: "Department",
          infoTooltip: "Organize departments and teams",
          count: departments.length,
          data: departments,
          searchTerm: searchTerms.department,
        },
        designation: {
          title: "Designation",
          infoTooltip: "Define job roles and positions",
          count: designations.length,
          data: designations,
          searchTerm: searchTerms.designation,
        },
        subDepartment: {
          title: "Sub-Department",
          infoTooltip: "Manage sub-departments within departments",
          count: subDepartments.length,
          data: subDepartments,
          searchTerm: searchTerms.subDepartment,
        },
        grade: {
          title: "Grade",
          infoTooltip: "Define employee grades and levels",
          count: grades.length,
          data: grades,
          searchTerm: searchTerms.grade,
        },
        category: {
          title: "Category",
          infoTooltip: followMinimumWages
            ? "Categorize employees. Enter Minimum Wages on Create/Edit, or click Set MW in the Min. Wages column."
            : "Categorize employees and positions",
          count: categories.length,
          data: categories,
          searchTerm: searchTerms.category,
        },
        reasons: {
          title: "Reasons",
          infoTooltip: "Manage resignation and termination reasons",
          count: resignationReasons.length + terminationReasons.length,
          data: [
            ...resignationReasons.map((reason) => ({
              ...reason,
              type: reason.type || "resignation",
            })),
            ...terminationReasons.map((reason) => ({
              ...reason,
              type: reason.type || "termination",
            })),
          ],
          searchTerm: searchTerms.reasons,
        },
      };

      const data = cardData[tabName];
      const filteredData = data.data.filter((item) => {
        const searchLower = data.searchTerm.toLowerCase();
        const matchesSearch =
          item.name.toLowerCase().includes(searchLower) ||
          (tabName === "branch" &&
            ((item.state || "").toLowerCase().includes(searchLower) ||
              (item.address || "").toLowerCase().includes(searchLower)));
        const matchesStatus = showDeactivated[tabName]
          ? true
          : item.status === "active" ||
            item.status === undefined ||
            item.status === null;
        return matchesSearch && matchesStatus;
      });

      return (
        <div
          key={tabName}
          className="relative z-[1] flex h-full min-h-0 flex-col overflow-hidden bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
        >
          {/* Card Header */}
          <div className="mb-3 flex shrink-0 items-start justify-between gap-3">
            <div>
              <p className="ds-small mb-0.5 font-bold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                Masters
              </p>
              <div className="flex items-center gap-2">
                <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">
                  {data.title}
                </h3>
                {data.infoTooltip && (
                <div className="group relative">
                  <Info
                    size={15}
                    className="cursor-pointer text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                  />

                  <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden w-72 -translate-y-1/2 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))] shadow-lg group-hover:block">
                    {data.infoTooltip}
                  </div>
                </div>)}
              </div>  
              <p className="ds-caption mt-0.5 text-[hsl(var(--ds-muted-fg))]">
                {data.description}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <DsButton
                type="button"
                size="sm"
                variant={showDeactivated[tabName] ? "secondary" : "outline"}
                onClick={() =>
                  setShowDeactivated((prev) => ({
                    ...prev,
                    [tabName]: !prev[tabName],
                  }))
                }
              >
                {showDeactivated[tabName] ? "Hide Inactive" : "Show Inactive"}
              </DsButton>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-3 shrink-0">
            <div className="relative">
              <DsIcon
                icon={Search}
                size="sm"
                className="pointer-events-none absolute left-2.5 top-1/2 z-[1] -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
              />
              <DsInput
                type="text"
                value={data.searchTerm}
                onChange={(e) => handleSearchChange(tabName, e.target.value)}
                placeholder={`Search ${data.title.toLowerCase()}...`}
                className="pl-8"
              />
            </div>
          </div>

          {/* Action Bar */}
          <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
            <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              {filteredData.length} of {data.count} items
            </span>
            {canCreateMaster ? (
              <div className="flex items-center gap-2">
                {tabName === "category" && followMinimumWages && (
                  <DsButton
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setMwImportOpen(true)}
                  >
                    Import MW
                  </DsButton>
                )}
                <DsButton
                  type="button"
                  size="sm"
                  onClick={() => handleCreateItem(tabName)}
                >
                  <DsIcon icon={Plus} size="sm" />
                  Create
                </DsButton>
              </div>
            ) : (
              <span
                className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--ds-muted-fg))]"
                title="Your role does not allow creating master records"
              >
                <Lock className="h-3 w-3" aria-hidden />
                View-only
              </span>
            )}
          </div>

          {/* Table Content */}
          <div className="mt-2 flex-1 min-h-0">
            {loading && data.data.length === 0 && (
              <div className="py-6 text-center">
                <p className="ds-body text-[hsl(var(--ds-muted-fg))]">Loading...</p>
              </div>
            )}
            {!loading && filteredData.length === 0 && (
              <div className="flex min-h-[168px] items-center justify-center py-4">
                <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
                  {data.searchTerm
                    ? "No matching items found"
                    : `No ${data.title.toLowerCase()} found`}
                </p>
              </div>
            )}
            {!loading && filteredData.length > 0 && (
              <div className="master-card-table-container flex h-full min-h-0 flex-1 flex-col">
                <div className="master-card-scrollable min-h-0 flex-1 overflow-x-hidden overflow-y-auto rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
                  <table className="ds-table w-full text-xs">
                    <thead className="sticky top-0 z-30 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]">
                      <tr>
                        <th className="sticky top-0 w-8 bg-[hsl(var(--ds-muted))] px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                          Sl. No
                        </th>
                        <th
                          className={`sticky top-0 bg-[hsl(var(--ds-muted))] px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))] ${
                            tabName === "reasons" ? "max-w-[80px]" : ""
                          }`}
                        >
                          Name
                        </th>
                        {tabName === "reasons" && (
                          <th className="sticky top-0 w-20 bg-[hsl(var(--ds-muted))] px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                            Type
                          </th>
                        )}
                        {tabName === "category" && followMinimumWages && (
                          <th className="sticky top-0 bg-[hsl(var(--ds-muted))] px-2 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                            Min. Wages
                          </th>
                        )}
                        {tabName === "branch" && (
                          <th className="sticky top-0 bg-[hsl(var(--ds-muted))] px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                            State
                          </th>
                        )}
                        <th className="sticky top-0 w-16 bg-[hsl(var(--ds-muted))] px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                          Status
                        </th>
                        <th className="sticky top-0 w-10 bg-[hsl(var(--ds-muted))] px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>                    </thead>
                    <tbody>
                      {filteredData.map((item, index) => (
                        <tr
                          key={item.id || index}
                          className="border-b border-[hsl(var(--ds-border))] transition-colors duration-150 hover:bg-[hsl(var(--ds-muted)/0.55)]"
                        >
                          <td className="w-8 px-2 py-2 text-xs text-[hsl(var(--ds-fg))]">
                            {index + 1}
                          </td>
                          <td
                            className={`truncate px-2 py-2 text-xs text-[hsl(var(--ds-fg))] ${
                              tabName === "reasons"
                                ? "max-w-[80px]"
                                : "max-w-[120px]"
                            }`}
                            title={item.name}
                          >
                            {item.name}
                          </td>
                          {tabName === "reasons" && (
                            <td className="w-20 px-2 py-2 text-xs">
                              <span
                                className={`whitespace-nowrap rounded px-1.5 py-0.5 text-xs capitalize ${
                                  item.type === "resignation"
                                    ? "rh-accent-soft dark:bg-redhalo-navy dark:text-redhalo-mint"
                                    : "border border-redhalo-orange/30 bg-redhalo-cream/80 text-redhalo-navy dark:border-redhalo-orange/40 dark:bg-redhalo-navy/50 dark:text-redhalo-warm"
                                }`}
                              >
                                {item.type || "resignation"}
                              </span>
                            </td>
                          )}
                          {tabName === "category" && followMinimumWages && (
                            <td className="px-2 py-2 text-right text-xs tabular-nums text-gray-800 dark:text-gray-200">
                              {categoryMwCurrentById[item.id] ? (
                                <button
                                  type="button"
                                  className="text-right hover:underline"
                                  title="Manage Minimum Wages history"
                                  onClick={() =>
                                    setCategoryMwModal({
                                      isOpen: true,
                                      category: item,
                                    })
                                  }
                                >
                                  {Number(
                                    categoryMwCurrentById[item.id].amount
                                  ).toLocaleString("en-IN")}
                                  {categoryMwCurrentById[item.id]
                                    ?.effectiveMonth ? (
                                    <div className="text-[10px] text-gray-500">
                                      from{" "}
                                      {
                                        categoryMwCurrentById[item.id]
                                          .effectiveMonth
                                      }
                                    </div>
                                  ) : null}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="rounded bg-redhalo-red/10 px-2 py-0.5 text-[11px] font-semibold text-redhalo-red hover:bg-redhalo-red/20"
                                  onClick={() =>
                                    setCategoryMwModal({
                                      isOpen: true,
                                      category: item,
                                    })
                                  }
                                >
                                  Set MW
                                </button>
                              )}
                            </td>
                          )}
                          {tabName === "branch" && (
                            <td
                              className="max-w-[100px] truncate px-2 py-2 text-xs text-[hsl(var(--ds-fg))]"
                              title={item.state || "Not set"}
                            >
                              {item.state || "—"}
                            </td>
                          )}
                          <td className="w-16 px-2 py-2 text-xs">
                            <DsBadge
                              tone={
                                item.status === "active" || !item.status
                                  ? "success"
                                  : "danger"
                              }
                            >
                              {item.status === "active" || !item.status
                                ? "Active"
                                : "Inactive"}
                            </DsBadge>
                          </td>
                          <td
                            className="w-10 px-2 py-2 text-xs"
                            style={{
                              position: "relative",
                              overflow: "visible",
                            }}
                          >
                            {canEditMaster ||
                            canDeactivateMaster ||
                            (tabName === "category" && followMinimumWages) ? (
                              <div
                                ref={openActionsKey === `${tabName}-${item.id}` ? openActionsRef : null}
                                className="relative"
                                style={{ position: "relative" }}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    setOpenActionsKey((prev) =>
                                      prev === `${tabName}-${item.id}` ? null : `${tabName}-${item.id}`
                                    )
                                  }
                                  className="relative z-10 rounded p-0.5 text-[hsl(var(--ds-secondary))] transition-colors hover:bg-[hsl(var(--ds-muted))]"
                                  aria-label="Row actions"
                                >
                                  <MoreVertical size={10} />
                                </button>
                                <div
                                  className={`action-dropdown-menu absolute right-0 top-full z-[999999] mt-1 w-28 rounded-md border border-gray-200 bg-white shadow-panel transition-all duration-200 dark:border-gray-700 dark:bg-[#2a2a2a] ${
                                    openActionsKey === `${tabName}-${item.id}` ? "visible opacity-100" : "invisible opacity-0"
                                  }`}
                                  style={{ zIndex: 999999 }}
                                >
                                  {canEditMaster && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenActionsKey(null);
                                        handleEditItem(tabName, item);
                                      }}
                                      className="flex w-full items-center px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                                    >
                                      <Edit size={8} className="mr-1" />
                                      Edit
                                    </button>
                                  )}
                                  {tabName === "category" &&
                                    followMinimumWages && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOpenActionsKey(null);
                                          setCategoryMwModal({
                                            isOpen: true,
                                            category: item,
                                          });
                                        }}
                                        className="flex w-full items-center px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                                      >
                                        <Settings size={8} className="mr-1" />
                                        Min. Wages
                                      </button>
                                    )}
                                  {canDeactivateMaster &&
                                    (item.status === "active" || !item.status ? (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOpenActionsKey(null);
                                          handleDeactivateItem(tabName, item);
                                        }}
                                        className="flex w-full items-center px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                                      >
                                        <Settings size={8} className="mr-1" />
                                        Deactivate
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOpenActionsKey(null);
                                          handleReactivateItem(tabName, item);
                                        }}
                                        className="flex w-full items-center px-2 py-1.5 text-left text-xs text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                                      >
                                        <Settings size={8} className="mr-1" />
                                        Reactivate
                                      </button>
                                    ))}
                                {/* <button
                                  type="button"
                                  onClick={() => {
                                    setOpenActionsKey(null);
                                    handleDeleteItem(tabName, item);
                                  }}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-red-600"
                                >
                                  <Trash2 size={8} className="mr-1" />
                                  Delete
                                </button> */}
                                </div>
                              </div>
                            ) : (
                              <span
                                className="inline-flex items-center text-[10px] text-gray-400 dark:text-gray-500"
                                title="Your role does not allow editing or deactivating master records"
                              >
                                <Lock className="h-3 w-3" aria-hidden />
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <style>{`
        .master-card-scrollable {
          overflow-x: hidden;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: hsl(var(--ds-border)) transparent;
          position: relative;
        }
        .master-card-scrollable::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .master-card-scrollable::-webkit-scrollbar-track {
          background: hsl(var(--ds-muted));
          border-radius: 4px;
        }
        .master-card-scrollable::-webkit-scrollbar-thumb {
          background-color: hsl(var(--ds-border));
          border-radius: 4px;
        }
        .master-card-scrollable::-webkit-scrollbar-thumb:hover {
          background-color: hsl(var(--ds-muted-fg) / 0.45);
        }
        .master-card-scrollable::-webkit-scrollbar-corner {
          background: transparent;
        }
        .action-dropdown-menu {
          z-index: 999999 !important;
          position: absolute !important;
          pointer-events: auto !important;
        }
        .master-card-table-container {
          position: relative !important;
          z-index: 1;
          min-height: 0;
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .master-card-table-container .master-card-scrollable {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          position: relative;
          min-height: 0;
          flex: 1 1 auto;
        }
        .master-card-table-container .master-card-scrollable table thead {
          position: sticky !important;
          top: 0 !important;
          z-index: 100 !important;
        }
        .master-card-table-container .master-card-scrollable table thead tr {
          position: relative;
        }
        .master-card-table-container .master-card-scrollable table thead th {
          position: sticky !important;
          top: 0 !important;
          z-index: 101 !important;
        }
        .master-card-table-container .master-card-scrollable table {
          position: relative;
        }
        .master-card-table-container .master-card-scrollable table tbody tr {
          position: relative;
        }
        .master-card-table-container .master-card-scrollable table tbody tr td:last-child {
          position: relative !important;
          overflow: visible !important;
          z-index: 10 !important; /* keep icon under sticky header */
        }
        .master-card-table-container .master-card-scrollable table tbody tr td:last-child .group {
          position: relative !important;
          z-index: 11 !important;
        }
        .master-card-table-container .master-card-scrollable table tbody tr td:last-child .action-dropdown-menu {
          position: absolute !important;
          z-index: 999999 !important;
          will-change: opacity, visibility;
          transform: translateZ(0);
          backface-visibility: hidden;
          isolation: isolate;
        }
        .embedded-master-pane > div {
          height: 100% !important;
          border: 0 !important;
          box-shadow: none !important;
          background: transparent !important;
          color: inherit !important;
          padding: 0 !important;
        }
      `}</style>
        <div
          className={
            isEmbedded
              ? "flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
              : "flex h-[calc(100dvh-5rem)] min-h-0 flex-1 flex-col overflow-hidden bg-[hsl(var(--ds-bg))] text-[hsl(var(--ds-fg))]"
          }
        >
          {/* Fixed Header with Tabs (hide when only one tab is allowed) */}
          {tabs.length > 1 && (
            <div className="sticky top-0 z-50 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
              <div className="flex border-b border-[hsl(var(--ds-border))]">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`px-6 py-3 text-sm font-semibold transition-colors ${
                      activeTab === tab
                        ? "border-b-2 border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-secondary))]"
                        : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === "enterprise" ? "Enterprise" : "Masters"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Content */}
          <div
            className={
              isEmbedded
                ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                : activeTab === "masters"
                  ? "flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6 pt-4 sm:px-8 sm:pb-8 sm:pt-5 lg:px-10 lg:pb-10 lg:pt-7"
                  : "min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3 sm:px-6 sm:pb-6 sm:pt-4 lg:px-8 lg:pb-8 lg:pt-6"
            }
          >
            {activeTab === "enterprise" && <Enterprise darkMode={darkMode} />}

            {activeTab === "masters" && (
              <div className="ds-masters-split">
                <nav className="ds-masters-rail" aria-label="Masters">
                  <div className="mb-1 px-2 pb-1 pt-0.5">
                    <h4 className="ds-small font-bold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Masters
                    </h4>
                  </div>
                  {masterTabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveMasterSubTab(tab)}
                      className={
                        activeMasterSubTab === tab
                          ? "ds-page-nav-item ds-page-nav-item-active"
                          : "ds-page-nav-item"
                      }
                    >
                      {tab === "subDepartment"
                        ? "Sub Department"
                        : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </nav>
                <div className="ds-masters-pane embedded-master-pane">
                  {renderMasterCard(activeMasterSubTab)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals for each master type */}
        {masterTabs.map((masterType) => {
          const modalState = modalStates[masterType];
          if (!modalState.isOpen) return null;

          return (
            <Modal
              key={masterType}
              darkMode={darkMode}
              isOpen={modalState.isOpen}
              onClose={() => handleModalClose(masterType)}
              title={
                modalState.editing
                  ? `Edit ${
                      masterType.charAt(0).toUpperCase() + masterType.slice(1)
                    }`
                  : `Create ${
                      masterType.charAt(0).toUpperCase() + masterType.slice(1)
                    }`
              }
              onSubmit={() => handleModalSubmit(masterType)}
              submitText={modalState.editing ? "Update" : "Save"}
              onCancel={() => handleModalClose(masterType)}
              maxWidthClass={
                masterType === "category" && followMinimumWages
                  ? "max-w-lg"
                  : "max-w-md"
              }
            >
              <input
                type="text"
                value={modalState.data.name}
                onChange={(e) =>
                  setModalStates((prev) => ({
                    ...prev,
                    [masterType]: {
                      ...prev[masterType],
                      data: { ...prev[masterType].data, name: e.target.value },
                    },
                  }))
                }
                placeholder={`${
                  masterType.charAt(0).toUpperCase() + masterType.slice(1)
                } Name`}
                className={`w-full mb-4 rounded-md border p-2 text-sm ${
                  darkMode
                    ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                    : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                }`}
              />
              {masterType === "category" && followMinimumWages && (
                <div
                  className={`mb-4 space-y-3 rounded-lg border p-3 ${
                    darkMode
                      ? "border-amber-500/30 bg-amber-950/20"
                      : "border-amber-200 bg-amber-50"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold ${
                      darkMode ? "text-amber-100" : "text-amber-900"
                    }`}
                  >
                    Minimum Wages
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                        Branch (MW applies to)
                      </label>
                      <select
                        value={modalState.data.mwBranchId ?? ""}
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                mwBranchId: e.target.value,
                              },
                            },
                          }))
                        }
                        className={`w-full rounded-md border p-2 text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0]"
                            : "border-gray-300 bg-white text-gray-900"
                        }`}
                      >
                        <option value="">All branches (company-wide)</option>
                        {(branches || []).map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                      <p className={`mt-1 text-[10px] ${darkMode ? "text-amber-200/70" : "text-amber-800/80"}`}>
                        Same category can have different minimum wages per branch. Salary structure uses the
                        employee&apos;s branch when resolving MW.
                      </p>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                        Amount (₹)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={modalState.data.minimumWage ?? ""}
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                minimumWage: e.target.value,
                              },
                            },
                          }))
                        }
                        placeholder="e.g. 18000"
                        className={`w-full rounded-md border p-2 text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0]"
                            : "border-gray-300 bg-white text-gray-900"
                        }`}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                        Effective month
                      </label>
                      <input
                        type="month"
                        value={
                          modalState.data.mwEffectiveMonth ||
                          new Date().toISOString().slice(0, 7)
                        }
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                mwEffectiveMonth: e.target.value,
                              },
                            },
                          }))
                        }
                        className={`w-full rounded-md border p-2 text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0]"
                            : "border-gray-300 bg-white text-gray-900"
                        }`}
                      />
                    </div>
                  </div>
                  {modalState.editing && (
                    <button
                      type="button"
                      className="text-xs font-semibold text-redhalo-red underline"
                      onClick={() => {
                        setCategoryMwModal({
                          isOpen: true,
                          category: modalState.editing,
                        });
                      }}
                    >
                      View / manage MW history
                    </button>
                  )}
                </div>
              )}
              {masterType === "branch" && (
                <>
                  <label
                    className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Address
                  </label>
                  <input
                    type="text"
                    value={modalState.data.address || ""}
                    onChange={(e) =>
                      setModalStates((prev) => ({
                        ...prev,
                        [masterType]: {
                          ...prev[masterType],
                          data: {
                            ...prev[masterType].data,
                            address: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="Address"
                    className={`mb-4 w-full rounded-md border p-2 text-sm ${
                      darkMode
                        ? "border-[#4C4C4C] bg-[#2C2C2C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                    }`}
                  />
                  <label
                    className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    State
                  </label>
                  <select
                    value={modalState.data.state || ""}
                    onChange={(e) =>
                      setModalStates((prev) => ({
                        ...prev,
                        [masterType]: {
                          ...prev[masterType],
                          data: {
                            ...prev[masterType].data,
                            state: e.target.value,
                          },
                        },
                      }))
                    }
                    className={`mb-4 w-full rounded-md border p-2 text-sm ${
                      darkMode
                        ? "border-[#4C4C4C] bg-[#2C2C2C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                    }`}
                  >
                    <option value="">Select state…</option>
                    {INDIAN_STATES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {masterType === "department" && (
                <select
                  value={modalState.data.departmentHead || ""}
                  onChange={(e) =>
                    setModalStates((prev) => ({
                      ...prev,
                      [masterType]: {
                        ...prev[masterType],
                        data: {
                          ...prev[masterType].data,
                          departmentHead: e.target.value,
                        },
                      },
                    }))
                  }
                  className={`w-full mb-4 rounded-md border p-2 text-sm ${
                    darkMode
                      ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                      : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                  }`}
                >
                  <option value="">Select Department Head</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.user.name} ({emp.empCode})
                    </option>
                  ))}
                </select>
              )}
              {masterType === "designation" && (
                <>
                  <select
                    value={modalState.data.departmentId || ""}
                    onChange={(e) =>
                      setModalStates((prev) => ({
                        ...prev,
                        [masterType]: {
                          ...prev[masterType],
                          data: {
                            ...prev[masterType].data,
                            departmentId: e.target.value,
                          },
                        },
                      }))
                    }
                    className={`w-full mb-4 rounded-md border p-2 text-sm ${
                      darkMode
                        ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                    }`}
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                        Salary Range From
                      </label>
                      <input
                        type="number"
                        value={modalState.data.salaryRangeFrom || ""}
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                salaryRangeFrom: e.target.value,
                              },
                            },
                          }))
                        }
                        className={`w-full rounded-md border p-2 text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        }`}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                        Salary Range To
                      </label>
                      <input
                        type="number"
                        value={modalState.data.salaryRangeTo || ""}
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                salaryRangeTo: e.target.value,
                              },
                            },
                          }))
                        }
                        className={`w-full rounded-md border p-2 text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                      Job Description
                    </label>
                    <textarea
                      type="textarea"
                      value={modalState.data.jobDescription?.heading || ""}
                      rows={4}
                      draggable={true}
                      onChange={(e) =>
                        setModalStates((prev) => ({
                          ...prev,
                          [masterType]: {
                            ...prev[masterType],
                            data: {
                              ...prev[masterType].data,
                              jobDescription: {
                                ...prev[masterType].data.jobDescription,
                                heading: e.target.value,
                              },
                            },
                          },
                        }))
                      }
                      className={`w-full rounded-md border p-2 text-sm ${
                        darkMode
                          ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                          : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                      }`}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                        Experience
                      </label>
                      <input
                        type="text"
                        value={modalState.data.jobDescription?.experience || ""}
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                jobDescription: {
                                  ...prev[masterType].data.jobDescription,
                                  experience: e.target.value,
                                },
                              },
                            },
                          }))
                        }
                        className={`w-full rounded-md border p-2 text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        }`}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                        Qualifications
                      </label>
                      <input
                        type="text"
                        value={
                          modalState.data.jobDescription?.qualifications || ""
                        }
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                jobDescription: {
                                  ...prev[masterType].data.jobDescription,
                                  qualifications: e.target.value,
                                },
                              },
                            },
                          }))
                        }
                        className={`w-full rounded-md border p-2 text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        }`}
                      />
                    </div>
                  </div>
                </>
              )}
              {masterType === "subDepartment" && (
                <>
                  <select
                    value={modalState.data.departmentId || ""}
                    onChange={(e) =>
                      setModalStates((prev) => ({
                        ...prev,
                        [masterType]: {
                          ...prev[masterType],
                          data: {
                            ...prev[masterType].data,
                            departmentId: e.target.value,
                          },
                        },
                      }))
                    }
                    className={`w-full mb-4 rounded-md border p-2 text-sm ${
                      darkMode
                        ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                    }`}
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {/* {masterType === "grade" && (
              <input
                type="text"
                value={modalState.data.name}
                onChange={(e) =>
                  setModalStates((prev) => ({
                    ...prev,
                    [masterType]: {
                      ...prev[masterType],
                      data: { ...prev[masterType].data, name: e.target.value },
                    },
                  }))
                }
                placeholder="Grade Name"
                className={`w-full mb-4 rounded-md border p-2 text-sm ${
                  darkMode
                    ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                    : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                }`}
              />
            )}
            {masterType === "category" && (
              <input
                type="text"
                value={modalState.data.name}
                onChange={(e) =>
                  setModalStates((prev) => ({
                    ...prev,
                    [masterType]: {
                      ...prev[masterType],
                      data: { ...prev[masterType].data, name: e.target.value },
                    },
                  }))
                }
                placeholder="Category Name"
                className={`w-full mb-4 rounded-md border p-2 text-sm ${
                  darkMode
                    ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                    : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                }`}
              />
            )} */}
              {masterType === "reasons" && (
                <select
                  value={modalState.data.type || "resignation"}
                  onChange={(e) =>
                    setModalStates((prev) => ({
                      ...prev,
                      [masterType]: {
                        ...prev[masterType],
                        data: {
                          ...prev[masterType].data,
                          type: e.target.value,
                        },
                      },
                    }))
                  }
                  className={`w-full mb-4 rounded-md border p-2 text-sm ${
                    darkMode
                      ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                      : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                  }`}
                >
                  <option value="resignation">Resignation</option>
                  <option value="termination">Termination</option>
                </select>
              )}
            </Modal>
          );
        })}

        {(isCreateCompanyOpen || isEditCompanyOpen) && (
          <CompanyModal
            darkMode={darkMode}
            isEdit={isEditCompanyOpen}
            formData={formData}
            inviteAdmin={inviteAdmin}
            handleInputChange={handleInputChange}
            handleLogoUpload={handleLogoUpload}
            handleInviteAdminChange={handleInviteAdminChange}
            handleSubmit={
              isEditCompanyOpen
                ? handleUpdateCompany
                : handleCreateCompanySubmit
            }
            handleSaveNew={handleSaveNew}
            handleReset={handleReset}
            handleClose={() =>
              isEditCompanyOpen
                ? setIsEditCompanyOpen(false)
                : handleCreateCompanyClose()
            }
          />
        )}

        <CategoryMinimumWagesModal
          darkMode={darkMode}
          isOpen={categoryMwModal.isOpen}
          category={categoryMwModal.category}
          canEdit={canEditMaster}
          onClose={() => setCategoryMwModal({ isOpen: false, category: null })}
          onUpdated={() => {
            // Force MW column refresh
            setCategories((prev) => [...prev]);
          }}
        />

        <CategoryMinimumWagesImportModal
          darkMode={darkMode}
          open={mwImportOpen}
          onClose={() => setMwImportOpen(false)}
          categories={categories}
          branches={branches}
          onImported={() => setCategories((prev) => [...prev])}
        />

        <Modal
          darkMode={darkMode}
          isOpen={confirmState.isOpen}
          onClose={() =>
            setConfirmState({ isOpen: false, masterType: null, item: null })
          }
          title="Confirm Delete"
          onSubmit={handleConfirmDelete}
          submitText="Delete"
          onCancel={() =>
            setConfirmState({ isOpen: false, masterType: null, item: null })
          }
        >
          <p className={darkMode ? "text-gray-200" : "text-gray-700"}>
            {confirmState.item
              ? `Are you sure you want to delete ${confirmState.item.name}?`
              : "Are you sure you want to delete this item?"}
          </p>
        </Modal>

        <Modal
          darkMode={darkMode}
          isOpen={duplicateInactiveState.isOpen}
          onClose={() =>
            setDuplicateInactiveState({
              isOpen: false,
              masterType: null,
              existing: null,
            })
          }
          title="Already Exists"
          onSubmit={handleActivateDuplicate}
          submitText="Activate"
          onCancel={() =>
            setDuplicateInactiveState({
              isOpen: false,
              masterType: null,
              existing: null,
            })
          }
        >
          <p className={darkMode ? "text-gray-200" : "text-gray-700"}>
            {duplicateInactiveState.existing?.name
              ? `${duplicateInactiveState.existing.name} already exists but is inactive. Do you want to activate it?`
              : "This item already exists but is inactive. Do you want to activate it?"}
          </p>
        </Modal>
      </div>
    );
}

export default Masters;
